import React, { Component } from 'react';
import { connect } from 'react-redux';
import styled, { withTheme } from 'styled-components';
import { Grid, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { push } from 'connected-react-router';
import { get } from 'lodash';
import { fetchMeta as fetchEntityMeta } from 'services/entities/duck';
import LabGeo from 'assets/lab-geo.png';
import LabTargeting from 'assets/lab-targeting.png';
import LabATV from 'assets/lab-atv.png';
import ProtectedComponent from 'hoc/ProtectedComponent';
import { DASHBOARD_LABS_RV_VIEW, DASHBOARD_LABS_CC_VIEW } from 'constants/capabilities';

const TileCol = styled(Col)`
    ::after{
        content: '';
        width: 1;
        height: 100%;
        position: absolute;
        border:  ${props => (props.endPosition !== 0 ? `1px solid ${props.theme.colors.lightgray}` : null)};
        top: 0;
        right: 0;
    }
`;

const LabWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    padding: 20px 0px;
`;

const LabTitle = styled.div`
    font-size: 22px;
    font-weight: bold;
    padding-bottom: 10px;
`;

const LabIcon = styled.img`
    display: block;
    width: 55px;
    height: 55px;
`;

const LabButton = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px 0px;
    font-size: 16px;
    font-weight: bold;
    color: #fff;
    width: 100%;
    max-width: 300px;
    background-color: ${props => (props.disabled ? props.theme.colors.gray : props.theme.colors.primary)};
    cursor: pointer;
`;

const LabLinkButton = styled(Link)`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px 0px;
    font-size: 16px;
    font-weight: bold;
    color: #fff;
    width: 100%;
    max-width: 300px;
    background-color: ${props => (props.disabled ? props.theme.colors.gray : props.theme.colors.primary)};
    cursor: pointer;
`;

const LabContent = styled.div`
    font-size: 12px;
    width: 100%;
    max-width: 300px;
    padding-bottom: 10px;
    min-height: 80px;
`;


class LabsContentContainer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            entityId: null
        };
    }

    static getDerivedStateFromProps(props, state) {
        const entityId = parseInt(props.entityId || props.match.params.entityId, 10);
        if (entityId !== state.entityId) {
            return {
                ...state,
                entityId
            };
        }

        return null;
    }

    static renderTile({ Button, ...elem }) {
        return (
            <LabWrapper>
                <LabIcon src={elem.icon} />
                <LabTitle>{elem.title}</LabTitle>
                <LabContent>{elem.content}</LabContent>
                <Button
                    to={elem.linkUrl}
                    target='_blank'
                    title={elem.linkAlt}
                >
                    {elem.linkTitle}
                </Button>
            </LabWrapper>
        );
    }

    static generateTile(tile, idx) {
        const Tile = ({ check }) => (
            <TileCol endPosition={(idx + 1) % 3} key={tile.title} xs={4}>
                {LabsContentContainer.renderTile(check ? tile : tile.defaultTile)}
            </TileCol>);

        const options = {
            alwaysRender: true,
            requiredCapabilities: tile.scope,
            onRenderProps: props => props
        };
        const ComponentTile = ProtectedComponent(options)(Tile);
        return <ComponentTile key={`tile.title-${idx}`} />;
    }

    getCurrentEntityId() {
        return this.state.entityId;
    }

    createPayload(entityId) {
        return {
            transactionId: `entity-${entityId}-labs`,
            entityId
        };
    }

    makeRequestsForData() {
        const entityId = this.getCurrentEntityId();

        const payload = this.createPayload(entityId);

        // HACK: Only trigger an API call if the data has not been previously pulled
        if (this.props.entities.meta.id !== entityId) {
            this.props.fetchEntityMeta(payload);
        }
    }

    componentDidMount() {
        this.makeRequestsForData();
    }

    componentDidUpdate(prevProps, prevState) {
        const isEntityChanged = prevState.entityId !== this.getCurrentEntityId();
        const isTimeRangeChanged = prevProps.selectedTimeRange !== this.props.selectedTimeRange;

        if (isTimeRangeChanged || isEntityChanged) {
            this.makeRequestsForData();
        }
    }

    render() {
        /*
            NOTE: These values are pulled from the API when the `env.js` file is returned to the
                  client via the Hapi Server. They are set with the ApplicationSettings Model.
        */
        const { entityId } = this.props;

        const defaultTile = {
            icon: LabTargeting,
            title: 'Advanced Targeting',
            content: `Using Advanced Targeting technologies and
            methods we are able to align your creative messaging with
            the proper audience.`,
            Button: () => <LabButton disabled>Pending Data</LabButton>,
            scope: [DASHBOARD_LABS_RV_VIEW]
        };
        const tiles = [
            {
                icon: LabTargeting,
                title: 'RV Attribution',
                content: `Look at the attribution data that runs our
                RealValue algo to understand how spend effects your
                adgroups attributed performance.`,
                Button: () => (<LabLinkButton
                    to={`/dashboard/${entityId}/labs/realvalue`}
                    title='Visit RV Insights'>VISIT LAB</LabLinkButton>),
                scope: [DASHBOARD_LABS_RV_VIEW],
                defaultTile
            },
            {
                icon: LabGeo,
                title: 'The Power of Multiple Channels',
                content: `Compare lift across households exposed to a single tactic
                with those exposed to multiple tactics.`,
                Button: () => (<LabLinkButton
                    to={`/dashboard/${entityId}/labs/cross-channel/all`}
                    title='Visit Multi Channel Lab'>VISIT LAB</LabLinkButton>),
                defaultTile
            },
            {
                icon: LabATV,
                title: 'ATV Cross Channel Lift',
                content: 'See how Advanced TV contributes to a lift in conversion rate per household.',
                Button: () => <LabLinkButton
                    to={`/dashboard/${entityId}/labs/cross-channel/atv`}
                    title='Visit ATV Lift Insights'>VISIT LAB</LabLinkButton>,
                scope: [DASHBOARD_LABS_CC_VIEW],
                defaultTile
            }
        ];

        return (
            <Grid fluid>
                <Row className="hidden-print">
                    {tiles.map(LabsContentContainer.generateTile)}
                </Row>
            </Grid>
        );
    }
}

const mapStateToProps = state => ({
    entityMeta: state.entities.meta,
    entities: state.entities,
    selectedTimeRange: state.config.selectedTimeRange,
    geoAnalysisOverrideEntityId: get(state, ['config', 'env', 'LABS_GEO_ANALYSIS_OVERRIDE_ENTITY_ID']),
    geoAnalysisOverrideDateRange: get(state, ['config', 'env', 'LABS_GEO_ANALYSIS_OVERRIDE_DATE_RANGE'])
});

export default connect(mapStateToProps, {
    fetchEntityMeta, push
})(withTheme(LabsContentContainer));



// WEBPACK FOOTER //
// ./src/containers/labs/LabsContentContainer.js